import React from "react";
import {
  ArrowUpRight,
  Sparkles,
  TrendingUp,
  Search,
} from "lucide-react";
import { projects } from "../data/projects";
import CountUp from "react-countup";

export const Hero = () => {
  const stats = [
    {
      end: 5,
      suffix: "+",
      label: "Years Experience",
    },
    {
      end: 100,
      suffix: "+",
      label: "Projects Delivered",
    },
    {
      end: 92,
      suffix: "%",
      label: "Client Satisfaction",
    },
  ];

  const performanceCards = [
    {
      label: "AI Visibility",
      end: 312,
      prefix: "+",
      suffix: "%",
    },
    {
      label: "Rankings",
      end: 3,
      prefix: "Top-",
      suffix: "",
    },
    {
      label: "Page Speed",
      end: 98,
      prefix: "",
      suffix: "/100",
    },
    {
      label: "Leads",
      end: 218,
      prefix: "+",
      suffix: "%",
    },
  ];

  return (
    <section className="relative overflow-hidden pt-32 pb-24 lg:pt-40 lg:pb-32">
      {/* Background */}
      <div
        className="absolute inset-0 grid-lines opacity-60"
        aria-hidden
      />

      <div
        className="absolute -top-24 -right-24 h-[520px] w-[520px] rounded-full brand-gradient blur-3xl opacity-30 blob-drift"
        aria-hidden
      />

      <div
        className="absolute bottom-0 -left-24 h-[380px] w-[380px] rounded-full bg-[hsl(var(--brand-amber)/.35)] blur-3xl opacity-70 float-slow"
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Left Content */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs font-medium text-brand-deep mb-6">
              <Sparkles className="h-3.5 w-3.5" />
              A part of Webify IT Solutions
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-[hsl(var(--brand-navy))]">
              Website Development & Marketing with{" "}
              <span className="text-gradient-brand">
                Affordable SEO Services
              </span>{" "}
              in USA
            </h1>

            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
              We don&apos;t just build websites - we build online
              growth systems. Combining SEO-focused development,
              AI-platform visibility and conversion-driven marketing
              so your website ranks higher, loads faster, and turns
              visitors into customers.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="#contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 rounded-full bg-[hsl(var(--brand-navy))] hover:bg-brand text-white px-6 py-3 text-sm font-semibold transition-all"
              >
                Get a Free Website Audit

                <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>

              <a
                href="/portfolio"
                data-testid="hero-cta-portfolio"
                className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--brand-violet)/.30)] text-brand-deep bg-white hover:bg-lilac px-6 py-3 text-sm font-semibold transition-all"
              >
                Explore {projects.length}+ Projects
              </a>
            </div>

            {/* Animated Stats */}
            <div className="mt-12 grid grid-cols-3 gap-4 sm:gap-8 max-w-xl">
              {stats.map((item, index) => (
                <div
                  key={item.label}
                  className="border-l-2 border-[hsl(var(--brand-amber))] pl-3 animate-counter-card"
                  style={{
                    animationDelay: `${index * 140}ms`,
                  }}
                >
                  <div className="font-[Fraunces] text-3xl sm:text-4xl font-bold text-[hsl(var(--brand-navy))]">
                    <CountUp
                      start={0}
                      end={item.end}
                      duration={2.4}
                      delay={0.2 + index * 0.15}
                      suffix={item.suffix}
                      enableScrollSpy
                      scrollSpyOnce
                      scrollSpyDelay={100}
                    />
                  </div>

                  <div className="text-xs text-slate-500 mt-1">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side Panel */}
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl glass p-6 card-shadow overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent pointer-events-none" />

              <div className="relative">
                <div className="flex items-center gap-2 pb-4 border-b border-black/5">
                  <div className="h-2.5 w-2.5 rounded-full bg-red-400" />
                  <div className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                  <div className="h-2.5 w-2.5 rounded-full bg-emerald-400" />

                  <div className="ml-3 text-[11px] font-mono text-slate-500">
                    rankifydev.com
                  </div>
                </div>

                <div className="mt-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Search className="h-4 w-4 text-brand" />
                      Organic Traffic
                    </div>

                    <div className="text-xs text-emerald-600 font-semibold">
                      ▲ Growing
                    </div>
                  </div>

                  {/* Animated Graph */}
                  <svg
                    viewBox="0 0 320 120"
                    className="w-full h-32 overflow-visible"
                  >
                    <defs>
                      <linearGradient
                        id="area"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="#7c3aed"
                          stopOpacity="0.35"
                        />

                        <stop
                          offset="100%"
                          stopColor="#7c3aed"
                          stopOpacity="0"
                        />
                      </linearGradient>
                    </defs>

                    <path
                      d="M0 90 L40 82 L80 74 L120 68 L160 54 L200 46 L240 32 L280 22 L320 8 L320 120 L0 120 Z"
                      fill="url(#area)"
                      className="animate-chart-area"
                    />

                    <path
                      d="M0 90 L40 82 L80 74 L120 68 L160 54 L200 46 L240 32 L280 22 L320 8"
                      fill="none"
                      stroke="#7c3aed"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      className="animate-chart-line"
                    />

                    <circle
                      cx="320"
                      cy="8"
                      r="5"
                      fill="#f59e0b"
                      className="animate-chart-dot"
                    />
                  </svg>

                  {/* Animated Performance Cards */}
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    {performanceCards.map((card, index) => (
                      <div
                        key={card.label}
                        className="rounded-xl bg-white/70 backdrop-blur border border-black/5 p-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg animate-metric-card"
                        style={{
                          animationDelay: `${400 + index * 140}ms`,
                        }}
                      >
                        <div className="text-[11px] text-slate-500">
                          {card.label}
                        </div>

                        <div className="text-lg font-bold text-brand-deep">
                          <CountUp
                            start={0}
                            end={card.end}
                            duration={2.2}
                            delay={0.5 + index * 0.15}
                            prefix={card.prefix}
                            suffix={card.suffix}
                            enableScrollSpy
                            scrollSpyOnce
                            scrollSpyDelay={100}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Badge */}
            <div className="absolute -top-6 -right-6 rounded-2xl bg-[hsl(var(--brand-navy))] text-white p-4 shadow-2xl float-slow hidden sm:block">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[hsl(var(--brand-amber))]">
                <TrendingUp className="h-4 w-4" />
                AI Search Ready
              </div>

              <div className="mt-1 text-sm font-semibold">
                Rank on Google & AI platforms
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes counterCardReveal {
          0% {
            opacity: 0;
            transform: translateY(18px);
          }

          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes metricCardReveal {
          0% {
            opacity: 0;
            transform: translateY(16px) scale(0.94);
          }

          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes chartLineDraw {
          0% {
            stroke-dashoffset: 500;
          }

          100% {
            stroke-dashoffset: 0;
          }
        }

        @keyframes chartAreaReveal {
          0% {
            opacity: 0;
            transform: translateY(12px);
          }

          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes chartDotPulse {
          0%,
          100% {
            transform: scale(1);
            opacity: 1;
          }

          50% {
            transform: scale(1.6);
            opacity: 0.65;
          }
        }

        .animate-counter-card {
          opacity: 0;
          animation: counterCardReveal 0.7s ease forwards;
        }

        .animate-metric-card {
          opacity: 0;
          animation: metricCardReveal 0.7s ease forwards;
        }

        .animate-chart-line {
          stroke-dasharray: 500;
          stroke-dashoffset: 500;
          animation: chartLineDraw 2.4s ease forwards;
        }

        .animate-chart-area {
          opacity: 0;
          transform-origin: bottom;
          animation: chartAreaReveal 1.4s ease 0.5s forwards;
        }

        .animate-chart-dot {
          transform-box: fill-box;
          transform-origin: center;
          animation:
            chartDotPulse 1.6s ease-in-out 2.2s infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-counter-card,
          .animate-metric-card,
          .animate-chart-line,
          .animate-chart-area,
          .animate-chart-dot {
            animation: none;
            opacity: 1;
            transform: none;
            stroke-dashoffset: 0;
          }
        }
      `}</style>
    </section>
  );
};

export default Hero;